/**
 * Basemap catalog for Map4Nomads “Change Map”.
 * Mix of Mapbox studio styles + free outdoor / weather XYZ tiles.
 */

export type BasemapId =
  | "standard"
  | "satellite"
  | "navigation"
  | "night"
  | "outdoor"
  | "hiking"
  | "foliage"
  | "weather";

export type BasemapDef = {
  id: BasemapId;
  label: string;
  blurb: string;
  kind: "mapbox" | "xyz";
  /** Mapbox style username/styleId */
  mapboxStyleId?: string;
  /** XYZ template with {z}/{x}/{y} (and optional {s} subdomain) */
  urlTemplate?: string;
  subdomains?: string[];
  attribution: string;
  maxZoom?: number;
};

const MAPBOX_TOKEN = process.env.GATSBY_MAPBOX_ACCESS_TOKEN || "";

export const BASEMAPS: BasemapDef[] = [
  {
    id: "standard",
    label: "Standard",
    blurb: "Clean Map4Nomads everyday map",
    kind: "mapbox",
    mapboxStyleId: "lececil123/cm1quujef00gt01pl4616glxy",
    attribution: "© Mapbox © OpenStreetMap",
  },
  {
    id: "satellite",
    label: "Satellite",
    blurb: "Aerial imagery for terrain & cover",
    kind: "mapbox",
    mapboxStyleId: "lececil123/cm1qui5d500ss01ph2qh3efh9",
    attribution: "© Mapbox © OpenStreetMap",
  },
  {
    id: "navigation",
    label: "Navigation",
    blurb: "Road-forward orientation",
    kind: "mapbox",
    mapboxStyleId: "lececil123/cm6kw85rl009w01sdb0hl1c5v",
    attribution: "© Mapbox © OpenStreetMap",
  },
  {
    id: "night",
    label: "Night",
    blurb: "Low-light basemap",
    kind: "mapbox",
    mapboxStyleId: "lececil123/cm6kvyvhi00ia01qr7ajegmwo",
    attribution: "© Mapbox © OpenStreetMap",
  },
  {
    id: "outdoor",
    label: "Outdoor topo",
    blurb: "Contours & relief — Mapy.cz-style hike read",
    kind: "xyz",
    urlTemplate: "https://tile.opentopomap.org/{z}/{x}/{y}.png",
    attribution: "© OpenTopoMap © OpenStreetMap",
    maxZoom: 17,
  },
  {
    id: "hiking",
    label: "Hiking trails",
    blurb: "Trail-forward outdoor map (Maps.me vibe)",
    kind: "xyz",
    urlTemplate: "https://outdoor.tiles.freemap.sk/{z}/{x}/{y}.jpg",
    attribution: "© Freemap.sk © OpenStreetMap",
    maxZoom: 19,
  },
  {
    id: "foliage",
    label: "Foliage",
    blurb: "Satellite view to read forests & green cover",
    kind: "xyz",
    // Esri World Imagery — vegetation reads clearly at mid zooms
    urlTemplate:
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: "© Esri, Maxar, Earthstar Geographics",
    maxZoom: 19,
  },
  {
    id: "weather",
    label: "Weather radar",
    blurb: "Live rain radar over a dark basemap",
    kind: "xyz",
    urlTemplate: "https://a.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png",
    attribution: "© CARTO © OpenStreetMap · Radar © RainViewer",
    maxZoom: 18,
  },
];

export const BASEMAP_BY_ID: Record<BasemapId, BasemapDef> = BASEMAPS.reduce(
  (acc, b) => {
    acc[b.id] = b;
    return acc;
  },
  {} as Record<BasemapId, BasemapDef>
);

export const DEFAULT_BASEMAP_ID: BasemapId = "standard";

/** Dark / imagery basemaps → use a light veil on unfocused countries. */
export function isDarkBasemap(id: BasemapId | string | null | undefined): boolean {
  return (
    id === "night" ||
    id === "weather" ||
    id === "satellite" ||
    id === "foliage"
  );
}

/** Migrate legacy localStorage Mapbox style ids → basemap ids */
export function resolveBasemapId(saved: string | null | undefined): BasemapId {
  if (!saved) return DEFAULT_BASEMAP_ID;
  if ((BASEMAP_BY_ID as Record<string, BasemapDef>)[saved]) {
    return saved as BasemapId;
  }
  const byMapbox = BASEMAPS.find((b) => b.mapboxStyleId === saved);
  if (byMapbox) return byMapbox.id;
  return DEFAULT_BASEMAP_ID;
}

export type TileProvider = (
  x: number,
  y: number,
  z: number,
  dpr?: number
) => string;

export function getBasemapProvider(id: BasemapId): TileProvider {
  const basemap = BASEMAP_BY_ID[id] || BASEMAP_BY_ID.standard;

  if (basemap.kind === "mapbox" && basemap.mapboxStyleId) {
    const styleId = basemap.mapboxStyleId;
    return (x, y, z, dpr = 1) =>
      `https://api.mapbox.com/styles/v1/${styleId}/tiles/512/${z}/${x}/${y}${
        dpr >= 2 ? "@2x" : ""
      }?access_token=${MAPBOX_TOKEN}`;
  }

  const template = basemap.urlTemplate || BASEMAP_BY_ID.standard.urlTemplate!;
  const subs = basemap.subdomains || ["a", "b", "c"];
  return (x, y, z) => {
    const s = subs[(x + y) % subs.length];
    return template
      .replace("{s}", s)
      .replace("{z}", String(z))
      .replace("{x}", String(x))
      .replace("{y}", String(y));
  };
}
